/**
 * Central Axios instance for the EHR Admin Portal.
 *
 * - baseURL: "/api/v1" (same-origin via nginx reverse proxy)
 * - Access + CSRF tokens live in memory (not localStorage)
 * - Refresh token lives in HttpOnly cookie (ehr_refresh)
 */
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type { ApiResponse, TokenPair } from "@/types";

const ACCESS_STORAGE_KEY = "admin_access_token";
const CSRF_STORAGE_KEY = "admin_csrf_token";
const REFRESH_STORAGE_KEY = "admin_refresh_token";
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

const resolvedApiOrigin = (
  import.meta.env.VITE_API_URL as string | undefined
)?.replace(/\/$/, "");
export const API_BASE_PATH = resolvedApiOrigin
  ? `${resolvedApiOrigin}/api/v1`
  : "/api/v1";

if (import.meta.env.DEV && resolvedApiOrigin) {
  console.warn(
    "[auth] VITE_API_URL is set — refresh cookies require same-origin /api (remove VITE_API_URL for local dev).",
  );
}

let accessToken: string | null = sessionStorage.getItem(ACCESS_STORAGE_KEY);
let csrfToken: string | null = sessionStorage.getItem(CSRF_STORAGE_KEY);

const apiClient = axios.create({
  baseURL: API_BASE_PATH,
  headers: { "Content-Type": "application/json" },
  timeout: 30_000,
  withCredentials: true,
});

/** Refresh this many ms before the access JWT expires (backend default TTL: 15 min). */
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

function applyAuthHeaders(config: InternalAxiosRequestConfig): void {
  config.headers = (config.headers ?? {}) as InternalAxiosRequestConfig["headers"];
  const headers = config.headers as Record<string, unknown>;
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const method = (config.method ?? "get").toLowerCase();
  if (csrfToken && MUTATING_METHODS.has(method)) {
    headers["X-CSRF-Token"] = csrfToken;
  }
}

function accessTokenExpiresAtMs(token: string): number | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const payload = JSON.parse(atob(segment.replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: number;
    };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isAccessTokenStale(token: string): boolean {
  const expMs = accessTokenExpiresAtMs(token);
  if (!expMs) return false;
  return Date.now() >= expMs - REFRESH_BUFFER_MS;
}

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function clearProactiveRefreshTimer(): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

function scheduleProactiveRefresh(): void {
  clearProactiveRefreshTimer();
  const token = accessToken;
  if (!token) return;

  const expMs = accessTokenExpiresAtMs(token);
  if (!expMs) return;

  const delay = Math.max(0, expMs - REFRESH_BUFFER_MS - Date.now());
  proactiveRefreshTimer = setTimeout(() => {
    void refreshSession()
      .then(() => scheduleProactiveRefresh())
      .catch(() => {
        /* next API call or 401 handler will surface auth failure */
      });
  }, delay);
}

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (
      accessToken &&
      isAccessTokenStale(accessToken) &&
      !config.url?.includes("/auth/refresh") &&
      !config.url?.includes("/auth/login")
    ) {
      try {
        await refreshSession();
      } catch {
        /* allow request to proceed; 401 handler retries once */
      }
    }
    applyAuthHeaders(config);
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

/** Single in-flight refresh — prevents rotation races from concurrent callers. */
let refreshPromise: Promise<string> | null = null;

async function refreshSession(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    // Send stored refresh token in body when available (cross-origin fallback
    // for when the HttpOnly cookie cannot cross eTLD+1 boundaries).
    const storedRefreshToken = sessionStorage.getItem(REFRESH_STORAGE_KEY);
    const body = storedRefreshToken ? { refresh_token: storedRefreshToken } : {};
    const { data } = await axios.post<ApiResponse<TokenPair>>(
      `${API_BASE_PATH}/auth/refresh`,
      body,
      { withCredentials: true },
    );
    const pair = data.data;
    const csrf = pair.csrf_token ?? csrfFromAccessToken(pair.access_token);
    if (!csrf) {
      throw new Error("Refresh response missing CSRF token");
    }
    setTokens(pair.access_token, csrf, pair.refresh_token);
    return pair.access_token;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (
        originalRequest.url?.includes("/auth/refresh") ||
        originalRequest.url?.includes("/auth/login")
      ) {
        clearTokens();
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        await refreshSession();
        applyAuthHeaders(originalRequest);
        return apiClient(originalRequest);
      } catch (refreshError) {
        clearTokens();
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      }
    }

    if (error.response) {
      const payload = error.response.data as ApiResponse<unknown> | undefined;
      const err = (payload as { error?: unknown })?.error;

      if (typeof err === "string") {
        return Promise.reject(new Error(err));
      }

      if (err && typeof err === "object") {
        const message = (err as { message?: string }).message ?? "Request failed";
        const details = (err as { details?: string }).details;
        return Promise.reject(new Error(details ? `${message}: ${details}` : message));
      }

      return Promise.reject(new Error("An unexpected error occurred"));
    }

    if (error.request) {
      return Promise.reject(
        new Error("Network error — please check your connection"),
      );
    }

    return Promise.reject(error);
  },
);

export function setTokens(nextAccessToken: string, nextCsrfToken: string, nextRefreshToken?: string): void {
  accessToken = nextAccessToken;
  csrfToken = nextCsrfToken;
  sessionStorage.setItem(ACCESS_STORAGE_KEY, nextAccessToken);
  sessionStorage.setItem(CSRF_STORAGE_KEY, nextCsrfToken);
  if (nextRefreshToken) {
    sessionStorage.setItem(REFRESH_STORAGE_KEY, nextRefreshToken);
  }
  scheduleProactiveRefresh();
}

export function clearTokens(): void {
  accessToken = null;
  csrfToken = null;
  sessionStorage.removeItem(ACCESS_STORAGE_KEY);
  sessionStorage.removeItem(CSRF_STORAGE_KEY);
  sessionStorage.removeItem(REFRESH_STORAGE_KEY);
  clearProactiveRefreshTimer();
}

if (accessToken) {
  scheduleProactiveRefresh();
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function csrfFromAccessToken(token: string): string | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const payload = JSON.parse(atob(segment.replace(/-/g, "+").replace(/_/g, "/"))) as {
      csrf?: string;
    };
    return typeof payload.csrf === "string" ? payload.csrf : null;
  } catch {
    return null;
  }
}

export async function trySilentRefresh(): Promise<boolean> {
  try {
    await refreshSession();
    return true;
  } catch {
    return false;
  }
}

export async function apiGet<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.get<ApiResponse<T>>(url, config);
  return data.data;
}

export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.post<ApiResponse<T>>(url, body, config);
  return data.data;
}

export async function apiPut<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.put<ApiResponse<T>>(url, body, config);
  return data.data;
}

export async function apiPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.patch<ApiResponse<T>>(url, body, config);
  return data.data;
}

export async function apiDelete<T = void>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.delete<ApiResponse<T>>(url, config);
  return data.data;
}

export async function apiGetPage<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<import("@/types").PaginatedResponse<T>> {
  const response = await apiClient.get<{
    data: T[];
    meta: import("@/types").PaginationMeta;
  }>(url, config);
  return {
    items: response.data.data,
    meta: response.data.meta,
  };
}

export async function apiGetRaw(
  url: string,
  config?: AxiosRequestConfig,
) {
  return apiClient.get(url, config);
}

export default apiClient;
